import { useTranslation } from 'react-i18next'
import { Upload, X, Plus, RefreshCw, Loader2 } from 'lucide-react'
import apiClient from '@/lib/api-client'

const GOOGLE_FONTS = [
  'auto', 'Roboto', 'Open Sans', 'Montserrat', 'Lato', 'Poppins',
  'Oswald', 'Raleway', 'Playfair Display', 'Merriweather', 'Nunito',
  'Inter', 'Bebas Neue', 'Archivo', 'DM Sans', 'Space Grotesk',
]

interface SlotStudioToolbarProps {
  refUrls: string[]
  generationCount: number
  resolution: string
  aspectRatio: string
  fontFamily: string
  ratioOptions: string[]
  resolutionOptions: string[]
  isBusy: boolean
  isPending: boolean
  canRerun: boolean
  onRefAdd: (url: string) => void
  onRefRemove: (i: number) => void
  onCountChange: (n: number) => void
  onResolutionChange: (v: string) => void
  onAspectRatioChange: (v: string) => void
  onFontChange: (v: string) => void
  onGenerate: () => void
}

function ToolbarSelect({ label, value, onChange, children }: {
  label: string; value: string; onChange: (v: string) => void; children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-0.5 shrink-0">
      <span className="text-3xs uppercase text-neutral-400 tracking-wide leading-none">{label}</span>
      <select value={value} onChange={e => onChange(e.target.value)} className="text-xs px-1.5 py-1 rounded border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200">
        {children}
      </select>
    </div>
  )
}

export function SlotStudioToolbar({
  refUrls, generationCount, resolution, aspectRatio, fontFamily,
  ratioOptions, resolutionOptions,
  isBusy, isPending, onRefAdd, onRefRemove,
  onCountChange, onResolutionChange, onAspectRatioChange, onFontChange, onGenerate,
}: SlotStudioToolbarProps) {
  const { t } = useTranslation()

  const uploadFile = async (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    try {
      const resp = await apiClient.post<{ id?: string; preview_url?: string }>('/api/v2/assets/upload', formData, {
        headers: { 'Content-Type': undefined },
      })
      const raw = resp.data.preview_url
      if (raw) onRefAdd(raw.startsWith('/') ? `${window.location.origin}${raw}` : raw)
    } catch { /* silent */ }
  }

  const handleUpload = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) await uploadFile(file)
    }
    input.click()
  }

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <button onClick={handleUpload} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-neutral-300 dark:border-neutral-600 rounded-lg hover:border-orange-400 transition-colors text-neutral-600 dark:text-neutral-300 shrink-0">
        <Upload className="w-3.5 h-3.5" />
        {t('amazonListing.slots.uploadSource')}
      </button>
      {refUrls.map((url, i) => (
        <div key={i} className="relative group shrink-0">
          <img src={url} alt="" className="w-12 h-12 rounded object-cover border border-neutral-200 dark:border-neutral-600 transition-all duration-150 group-hover:w-[60px] group-hover:h-[60px]" />
          <button onClick={() => onRefRemove(i)} className="absolute -top-1 -right-1 p-0.5 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity">
            <X className="w-2.5 h-2.5" />
          </button>
        </div>
      ))}
      {refUrls.length < 3 && (
        <button onClick={handleUpload} className="flex items-center gap-1 px-2 py-1 text-2xs text-orange-500 border border-dashed border-orange-300 rounded-full hover:bg-orange-50 dark:hover:bg-orange-500/10 shrink-0">
          <Plus className="w-3 h-3" />
          {t('amazonListing.slots.addReference')}
        </button>
      )}
      <div className="w-px h-5 bg-neutral-200 dark:bg-neutral-600 mx-1 shrink-0" />
      <ToolbarSelect label={t('amazonListing.slots.countLabel')} value={String(generationCount)} onChange={v => onCountChange(Number(v))}>
        {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n}</option>)}
      </ToolbarSelect>
      <ToolbarSelect label={t('amazonListing.slots.resolution')} value={resolution} onChange={onResolutionChange}>
        {resolutionOptions.map(r => <option key={r} value={r}>{r.toUpperCase()}</option>)}
      </ToolbarSelect>
      <ToolbarSelect label={t('amazonListing.slots.aspectRatio')} value={aspectRatio} onChange={onAspectRatioChange}>
        {ratioOptions.map(r => <option key={r} value={r}>{r}</option>)}
      </ToolbarSelect>
      <ToolbarSelect label={t('amazonListing.slots.fontFamily')} value={fontFamily} onChange={onFontChange}>
        {GOOGLE_FONTS.map(f => <option key={f} value={f}>{f === 'auto' ? t('amazonListing.slots.fontAuto') : f}</option>)}
      </ToolbarSelect>
      <div className="ml-auto shrink-0">
        <button onClick={onGenerate} disabled={isBusy || isPending} className="flex items-center gap-2 px-4 py-1.5 text-sm rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 font-semibold">
          {isBusy || isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {t('amazonListing.slots.regenerate')}
        </button>
      </div>
    </div>
  )
}
